import { type FC } from 'react';

import Icon from '@/components/UI/Icon';
import Table from '@/components/UI/Table';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { _isEmpty, _isNotBlank } from '@/littledash';

import type { SuccessfulOverviewSection } from '../../OverviewAccordion.model';
import overviewTableStyles from '../OverviewSectionTable.module.scss';

interface PresetSectionContentProps {
  section: SuccessfulOverviewSection<'preset'>;
}

type PresetCalculation = SuccessfulOverviewSection<'preset'>['rows'][number];
type PresetCalculationInput = PresetCalculation['inputs'][number];

const emptyValue = '-';

const formatNameWithUnit = ({ name, unit }: Pick<PresetCalculationInput, 'name' | 'unit'>): string =>
  _isNotBlank(unit) ? `${name} (${unit})` : name;

const renderBoolean = (value: boolean, testId: string) =>
  value ? <Icon icon="checkbox_thin" width="20" height="20" testId={testId} /> : emptyValue;

/**
 * Renders the preset calculations behind the Preset accordion section.
 */
const PresetSectionContent: FC<PresetSectionContentProps> = ({ section }) => {
  const calculations = section.rows;
  const columns: Array<TanStackColumnDef<PresetCalculation>> = [
    {
      id: 'measurement',
      header: 'Measurement',
      accessorKey: 'name',
      cell: ({ row: { original } }) => formatNameWithUnit(original),
    },
    {
      id: 'inputs',
      header: 'Inputs',
      accessorKey: 'inputs',
      cell: ({
        row: {
          original: { inputs },
        },
      }) => (_isEmpty(inputs) ? emptyValue : inputs.map(formatNameWithUnit).join(', ')),
    },
    {
      id: 'formula',
      header: 'Alert Formula',
      accessorKey: 'formula',
      cell: ({
        row: {
          original: { formula },
        },
      }) => (_isNotBlank(formula) ? formula : emptyValue),
    },
    {
      id: 'all-inputs-required',
      header: 'All inputs required',
      accessorKey: 'all_inputs_required',
      cell: ({
        row: {
          original: { all_inputs_required: allInputsRequired },
        },
      }) => renderBoolean(allInputsRequired, 'all-inputs-required'),
    },
    {
      id: 'auto-swap-enabled',
      header: 'Auto-swap enabled',
      accessorKey: 'auto_swap_enabled',
      cell: ({
        row: {
          original: { auto_swap_enabled: autoSwapEnabled },
        },
      }) => renderBoolean(autoSwapEnabled, 'auto-swap-enabled'),
    },
  ];

  return (
    <div data-test-component="PresetSectionContent" data-test-element="container">
      <Table data={calculations} columns={columns} maxHeightClassName={overviewTableStyles.maxHeight} />
    </div>
  );
};

export default PresetSectionContent;
